import { InputHTMLAttributes, forwardRef, useState } from 'react';
import { clsx } from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  showPasswordToggle?: boolean;
}

/**
 * Accessible input component
 * WCAG 2.1 AA compliant with proper labels and error states
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, showPasswordToggle = false, type, ...props }, ref) => {
    const { t } = useTranslation('common');
    const [showPassword, setShowPassword] = useState(false);
    const inputType = showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            className={clsx(
              'block w-full rounded-xl border border-border bg-muted/20 px-4 py-3',
              'text-sm font-medium text-foreground',
              'placeholder:text-muted-foreground',
              'ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium',
              'transition-all focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/10',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/10 focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              showPasswordToggle && 'pr-12',
              error && 'border-destructive focus:border-destructive focus:ring-destructive/20',
              className
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${props.id}-error` : undefined}
            {...props}
          />
          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center rounded pr-3 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/10 focus-visible:ring-offset-2"
              aria-label={showPassword ? t('shared.input.hidePassword') : t('shared.input.showPassword')}
              tabIndex={0}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {error && (
          <p
            id={`${props.id}-error`}
            className="mt-1 text-sm text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

